import { useCallback, useEffect, useRef, useState } from 'react';
import { useProject } from '@/features/project/hooks/use-projects';
import { goalsAutosaveCoordinator } from '@/features/project/lib/goals-autosave-coordinator';
import {
  convertFacadeArea,
  formatFacadeAreaValue,
  parseFacadeArea,
  type FacadeAreaUnit,
} from '@/features/project/ui/FacadeAreaField';

/**
 * Shared goals-editor buffer used by both the standalone goals page and the
 * embedded render-settings goals sheet.
 *
 * Save model (owner decision 2026-09-27, same on Apple / Android): everything
 * the user types is kept, with no save button and no "save changes?" prompt.
 * The buffer is saved when the user LEAVES the brief (page unmount, sheet
 * close via `save()`, tab hidden, page hide) and when a text field loses
 * focus. Never per keystroke and never on a timer: every save runs the
 * server-side brief evaluation, which must not fire mid-typing. `save()` is a
 * no-op without unsaved changes and skips a payload already sent, so blur
 * followed by leave is one PATCH. A failed save keeps its payload in the
 * coordinator; the next mount re-seeds from it and the next leave / foreground
 * retries it.
 *
 * Server reconcile (mirrors Apple's `ProjectGoalsView.loadProject`): the buffer
 * seeds from the project and afterwards adopts a CHANGED server value only for
 * the fields the user has not touched (buffer still equals the previous server
 * baseline). That keeps the goals-wipe guard (2026-06-02) — a refetch can never
 * trample what someone is typing — while fixing the stale-brief freeze: the
 * render-settings sheet is mounted (and therefore seeded) while still closed, so
 * a seed-once buffer latched onto whatever the cache held at panel-mount time —
 * including a stale IndexedDB snapshot — and showed that text forever, even
 * after the refetch landed. Worse, committing that frozen buffer wrote the old
 * brief back over the newer server one.
 */
export function useGoalsEditor(projectId: string) {
  const { data: projectResponse, isLoading } = useProject(projectId);
  const project = projectResponse?.data;

  const serverGoals = project?.attributes.goals ?? '';
  const serverRegion = project?.attributes.construction_region ?? '';
  const serverArea =
    project?.attributes.facade_area != null
      ? String(project.attributes.facade_area)
      : '';
  const serverUnit: FacadeAreaUnit =
    project?.attributes.facade_area_unit ?? 'm2';

  // A draft left behind by a failed save outranks the server value: it is
  // newer, and dropping it would lose what the user typed.
  const [draft] = useState(() => goalsAutosaveCoordinator.getPending(projectId));
  const [goals, setGoals] = useState(draft?.goals ?? serverGoals);
  const [region, setRegion] = useState(draft?.region ?? serverRegion);
  const [area, setArea] = useState(
    draft?.facadeArea !== undefined
      ? draft.facadeArea === null
        ? ''
        : formatFacadeAreaValue(draft.facadeArea)
      : serverArea
  );
  // Local mutable display unit — the toggle flips it immediately, before the
  // server round-trips. Seeded from the server unit and kept in sync until the
  // user overrides it (same plain-buffer reconcile pattern as the value).
  const [unit, setUnit] = useState<FacadeAreaUnit>(
    draft?.facadeAreaUnit ?? serverUnit
  );
  // Last server snapshot the buffer was reconciled against. `null` until the
  // project first loads (the seed). A field whose buffer still equals this
  // baseline is untouched and may adopt a newer server value.
  const baselineRef = useRef<{
    goals: string;
    region: string;
    area: string;
    unit: FacadeAreaUnit;
  } | null>(null);

  // Only an actually-edited area rides along in the PATCH — sending the field
  // (even unchanged) marks it user-owned and clears the "estimated" flag.
  const areaChanged = area.trim() !== serverArea;
  const unitChanged = unit !== serverUnit;
  const pendingArea = areaChanged ? parseFacadeArea(area) : undefined;

  // Seed on first load, then reconcile per field: adopt a changed server value
  // only where the buffer still matches the previous baseline (nothing typed).
  useEffect(() => {
    if (!project) return;
    const next = {
      goals: serverGoals,
      region: serverRegion,
      area: serverArea,
      unit: serverUnit,
    };
    const baseline = baselineRef.current;
    baselineRef.current = next;

    if (!baseline) {
      // Seed. A failed-save draft already seeded the buffer (useState init)
      // and must survive the first server load.
      if (!draft) {
        setGoals(next.goals);
        setRegion(next.region);
        setArea(next.area);
        setUnit(next.unit);
      }
      return;
    }

    if (next.goals !== baseline.goals && goals === baseline.goals) {
      setGoals(next.goals);
    }
    if (next.region !== baseline.region && region === baseline.region) {
      setRegion(next.region);
    }
    if (next.area !== baseline.area && area === baseline.area) {
      setArea(next.area);
    }
    if (next.unit !== baseline.unit && unit === baseline.unit) {
      setUnit(next.unit);
    }
  }, [
    draft,
    project,
    serverGoals,
    serverRegion,
    serverArea,
    serverUnit,
    goals,
    region,
    area,
    unit,
  ]);

  const isDirty =
    goals.trim() !== serverGoals.trim() ||
    region.trim() !== serverRegion.trim() ||
    areaChanged ||
    unitChanged;

  /**
   * Switch the display unit: convert the current buffer value (if numeric),
   * update the unit, and persist immediately — sending BOTH the converted
   * `facade_area` and the new `facade_area_unit` so the server stores it in the
   * unit the user picked. An empty/invalid buffer still switches the unit
   * (preference persists) without sending a numeric value.
   */
  const changeUnit = useCallback(
    (next: FacadeAreaUnit) => {
      if (next === unit) return;
      const parsed = parseFacadeArea(area);
      let converted: number | null | undefined = parsed;
      let nextArea = area;
      if (typeof parsed === 'number') {
        const value = convertFacadeArea(parsed, unit, next);
        nextArea = formatFacadeAreaValue(value);
        converted = value;
        setArea(nextArea);
      }
      setUnit(next);
      goalsAutosaveCoordinator.flushNow(
        projectId,
        goals,
        region,
        converted,
        next
      );
    },
    [unit, area, projectId, goals, region]
  );

  // Payload of the last save this editor fired. The server baseline only
  // catches up after the detail refetch, so `isDirty` stays true for a moment
  // after a save; this keeps blur-then-leave from sending the same brief twice.
  // A failed save leaves a pending draft in the coordinator, which re-arms it.
  const lastSentRef = useRef<string | null>(null);

  /**
   * Persist the buffer if it has unsaved changes (fires the server-side
   * evaluation). Called on field blur and on every way of leaving the brief.
   */
  const save = useCallback(() => {
    if (!isDirty) return;
    const facadeAreaUnit = unitChanged ? unit : undefined;
    const key = JSON.stringify([goals.trim(), region.trim(), pendingArea, facadeAreaUnit]);
    if (
      key === lastSentRef.current &&
      !goalsAutosaveCoordinator.hasPending(projectId)
    ) {
      return;
    }
    lastSentRef.current = key;
    goalsAutosaveCoordinator.flushNow(
      projectId,
      goals,
      region,
      pendingArea,
      facadeAreaUnit
    );
  }, [isDirty, projectId, goals, region, pendingArea, unitChanged, unit]);

  // Leaving the brief by any path the editor itself can observe: unmount
  // (in-app navigation, back), the tab going to the background, and page hide
  // (reload / tab close, best effort: the browser may drop the request). A
  // sheet host stays mounted while closed, so it calls `save()` on close.
  // Coming back to the foreground retries a save that failed earlier.
  const saveRef = useRef(save);
  saveRef.current = save;
  useEffect(() => {
    const onVisibility = () => {
      if (document.visibilityState === 'hidden') {
        saveRef.current();
      } else if (goalsAutosaveCoordinator.hasPending(projectId)) {
        void goalsAutosaveCoordinator.flushAndAwait(projectId);
      }
    };
    const onPageHide = () => saveRef.current();
    document.addEventListener('visibilitychange', onVisibility);
    window.addEventListener('pagehide', onPageHide);
    return () => {
      document.removeEventListener('visibilitychange', onVisibility);
      window.removeEventListener('pagehide', onPageHide);
      saveRef.current();
    };
  }, [projectId]);

  return {
    projectId,
    project,
    isLoading,
    goals,
    setGoals,
    region,
    setRegion,
    area,
    setArea,
    unit,
    changeUnit,
    areaChanged,
    isDirty,
    save,
  };
}
